import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../api';

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'normal' });
  const [saving, setSaving] = useState(false);

  const fetchTickets = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: '50', offset: String(offset) };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const data = await api.getTickets(params);
      setTickets(data.tickets);
      setPagination(data.pagination);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [statusFilter, priorityFilter]);

  useEffect(() => { fetchTickets(0); }, [fetchTickets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createTicket(form);
      setShowForm(false);
      setForm({ subject: '', description: '', category: 'general', priority: 'normal' });
      fetchTickets(0);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleStatusChange = async (ticket, newStatus) => {
    try {
      await api.updateTicket(ticket.id, { status: newStatus });
      setTickets((prev) => prev.map((t) => t.id === ticket.id ? { ...t, status: newStatus } : t));
    } catch { /* ignore */ }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Support Tickets</h1>
        <p>User feedback and support queue</p>
      </div>

      <div className="toolbar">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select className="filter-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New ticket'}
        </button>
      </div>

      {showForm && (
        <form className="card" style={{ marginBottom: 20 }} onSubmit={handleCreate}>
          <div className="form-group">
            <label>Subject</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea style={{ width: '100%', minHeight: 80, padding: 10, border: '1px solid #e0e0e0', borderRadius: 8, fontFamily: 'inherit', fontSize: 14 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Category</label>
              <select className="filter-select" style={{ width: '100%' }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature Request</option>
                <option value="account">Account</option>
                <option value="billing">Billing</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select className="filter-select" style={{ width: '100%' }} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create ticket'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="empty-state"><p>No tickets found.</p></div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead><tr><th>Subject</th><th>Category</th><th>Priority</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>{t.subject || t.description.slice(0, 50)}</td>
                    <td><span className="badge badge-gray">{t.category}</span></td>
                    <td><span className={`badge badge-${priorityColor(t.priority)}`}>{t.priority}</span></td>
                    <td>
                      <select className="filter-select" style={{ padding: '4px 8px', fontSize: 12 }} value={t.status} onChange={(e) => handleStatusChange(t, e.target.value)}>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td style={{ fontSize: 13, color: '#888' }}>{new Date(t.createdAt).toLocaleDateString('en-ZA')}</td>
                    <td>
                      {t.status !== 'resolved' && t.status !== 'closed' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => handleStatusChange(t, 'resolved')}>Resolve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function priorityColor(p) {
  switch (p) {
    case 'urgent': return 'red';
    case 'high': return 'yellow';
    case 'normal': return 'blue';
    case 'low': return 'gray';
    default: return 'gray';
  }
}
